/**
 * MenuPageCompact
 * ───────────────
 * Accordion-style /menu view used by tenants that flip
 * `business.menu_layout = "accordion"`. Reads categories + items via
 * the shared `useMenuData` hook so it stays in sync with the other
 * templates. Behaviours:
 *   - At most 3 hero images (hero + ≤ 2 inline gallery shots)
 *   - Categories rendered as shadcn `<Accordion type="multiple">`
 *   - The section whose `time_slots` includes the current slot is
 *     auto-opened (e.g. "Bigger Plates" at lunch)
 *   - Multi-size items use a dropdown; addons launch the existing
 *     AddonDialog via a Customise button
 *   - A `MenuSlotTabs` chip row drives the slot filter so a morning
 *     customer can preview the dinner menu
 */
import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { money } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { useCart } from "@/context/CartContext";
import { useMenuData } from "@/lib/useMenuData";
import MenuSlotTabs from "@/components/MenuSlotTabs";
import {
  useDietaryConflictMap, AllergenBadge, BlockedAddButton, conflictWrapperClass,
} from "@/lib/useDietaryConflictMap";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AddonDialog from "@/components/AddonDialog";

const SIZE_LABELS = {
  sml: "Small", small: "Small",
  reg: "Regular", regular: "Regular", med: "Medium", medium: "Medium",
  lrg: "Large", large: "Large",
  cup: "Cup (dine in)", pot: "Pot", mug: "Mug (dine in)",
  "6oz":  "6oz takeaway",  "12oz": "12oz takeaway",  "16oz": "16oz takeaway",
  "14oz": "14oz takeaway", "20oz": "20oz takeaway",
  "glass-r": "Regular glass (dine in)", "glass-l": "Large glass (dine in)",
  light: "Light", full: "Full",
};

function sortedSizeKeys(prices, columnsByKey) {
  const keys = Object.keys(prices || {});
  if (keys.length === 0) return [];
  if (columnsByKey && columnsByKey.length > 0) {
    const order = new Map(columnsByKey.map((k, i) => [k, i]));
    return keys.slice().sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99));
  }
  return keys.slice().sort();
}

export default function MenuPageCompact() {
  const { business } = useBusiness();
  const cart = useCart();
  const { cats, itemsByCat, loading, slot, setSlot } = useMenuData();
  const _allItems = useMemo(() => Object.values(itemsByCat || {}).flat(),
                                          [itemsByCat]);
  const conflictMap = useDietaryConflictMap(_allItems);
  const [open, setOpen] = useState([]);
  const [sizeChoice, setSizeChoice] = useState({});
  const [addonItem, setAddonItem] = useState(null);

  // Auto-open categories that match the active slot. When the user picks
  // "All menus" we just open the first two so the page never lands looking
  // empty.
  useEffect(() => {
    if (cats.length === 0) return;
    if (slot === "all") {
      setOpen(cats.slice(0, 2).map((c) => c.id));
      return;
    }
    const match = cats.filter((c) => {
      const slots = (itemsByCat[c.id] || []).flatMap((it) => it.time_slots || []);
      return slots.includes(slot);
    });
    setOpen(match.length > 0 ? match.map((c) => c.id) : cats.slice(0, 1).map((c) => c.id));
  }, [slot, cats, itemsByCat]);

  const hero = business?.hero_image_url || "";
  const gallery = (business?.gallery_images || []).filter(Boolean);
  const inlineImages = useMemo(
    () => gallery.filter((g) => g !== hero).slice(0, 2),
    [gallery, hero],
  );

  function quickAdd(item, sizeKey) {
    if (item.addons && item.addons.length > 0) {
      const cat = cats.find((c) => c.id === item.category_id) || null;
      setAddonItem({ ...item, _category: cat });
      return;
    }
    const price = sizeKey ? Number(item.prices?.[sizeKey]) || Number(item.price) : Number(item.price) || 0;
    cart.addLine({
      item_id: item.id,
      name: item.name,
      base_price: price,
      quantity: 1,
      selected_addons: sizeKey ? [{ name: `Size: ${SIZE_LABELS[sizeKey] || sizeKey}`, price: 0 }] : [],
      notes: "",
      calories: Number(item.calories) || 0,
      tags: item.tags || [],
      price_column: sizeKey || "",
      price_label: SIZE_LABELS[sizeKey] || "",
    });
    toast.success(`${item.name} added to cart`);
  }

  if (loading) {
    return (
      <div data-testid="menu-compact-loading" className="max-w-3xl mx-auto px-4 py-24 text-center text-[#5C625E]">
        Loading the menu…
      </div>
    );
  }

  return (
    <div data-testid="menu-compact-page" className="bg-[#FDFBF7]">
      {hero && (
        <div className="relative w-full h-[42vh] md:h-[55vh] overflow-hidden">
          <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-4">
            <p className="text-xs uppercase tracking-[0.25em] mb-3 opacity-90">{business?.tagline || "Menu"}</p>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-none">Menu</h1>
          </div>
        </div>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <MenuSlotTabs slot={slot} onChange={setSlot} />
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <Accordion
          type="multiple"
          value={open}
          onValueChange={setOpen}
          data-testid="menu-compact-accordion"
          className="space-y-2"
        >
          {cats.map((cat, idx) => {
            const rows = itemsByCat[cat.id] || [];
            if (rows.length === 0) return null;
            const colKeys = (cat.price_columns || []).map((c) => c.key);
            const inlineSrc = (idx === 1 && inlineImages[0]) || (idx === Math.floor(cats.length / 2) && inlineImages[1]) || null;
            return (
              <div key={cat.id}>
                {inlineSrc && (
                  <img src={inlineSrc} alt="" className="block w-full h-44 md:h-56 object-cover rounded-2xl my-6" loading="lazy" />
                )}
                <AccordionItem
                  value={cat.id}
                  data-testid={`menu-cat-${cat.id}`}
                  className="border border-[#E8E5DF] rounded-2xl bg-white px-5 md:px-6"
                >
                  <AccordionTrigger data-testid={`menu-cat-toggle-${cat.id}`} className="hover:no-underline">
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#5A7247] font-bold">
                        {cat.kind === "beverage" ? "Drinks" : cat.kind === "wine" ? "Wine" : "Food"}
                      </p>
                      <p className="font-serif-display text-2xl md:text-3xl text-[#121614] mt-0.5">{cat.name}</p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="divide-y divide-[#E8E5DF]">
                      {rows.map((it) => {
                        const keys = sortedSizeKeys(it.prices, colKeys);
                        const sizeKey = sizeChoice[it.id] || keys[0] || "";
                        const livePrice = sizeKey
                          ? Number(it.prices?.[sizeKey]) || Number(it.price) || 0
                          : Number(it.price) || 0;
                        const conflict = conflictMap.get(it.id);
                        return (
                          <li
                            key={it.id}
                            data-testid={`menu-item-${it.id}`}
                            title={conflict?.reasons?.join(" · ") || ""}
                            className={`py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-5
                                              ${conflictWrapperClass(conflict)}
                                              ${conflict?.hasAllergen
                                                  ? "bg-rose-50/30 rounded-lg px-2"
                                                  : ""}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-[#121614]">{it.name}</p>
                                <AllergenBadge conflict={conflict}
                                          testId={`compact-${it.id}-allergen`} />
                              </div>
                              {it.description && <p className="text-sm text-[#5C625E] mt-0.5">{it.description}</p>}
                              {(it.addons || []).length > 0 && (
                                <p className="text-[11px] uppercase tracking-[0.1em] text-[#C05746] mt-1.5 font-bold">
                                  {(it.addons || []).length} add-on{(it.addons || []).length === 1 ? "" : "s"} available
                                </p>
                              )}
                              {keys.length > 1 && !conflict?.hasAllergen && (
                                <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`size-chips-${it.id}`}>
                                  {keys.map((k) => (
                                    <button
                                      key={k}
                                      data-testid={`size-chip-${it.id}-${k}`}
                                      onClick={() => quickAdd(it, k)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#121614]/20 hover:border-[#121614] hover:bg-[#F3F0EA] text-[12px] font-semibold transition-colors"
                                    >
                                      <span>{SIZE_LABELS[k] || k}</span>
                                      <span className="tabular-nums text-[#5C625E]">{money(Number(it.prices?.[k]) || 0)}</span>
                                      <span className="ml-0.5 w-4 h-4 rounded-full bg-[#121614] text-white text-[10px] grid place-items-center">+</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {keys.length <= 1 && (
                                <span
                                  data-testid={`menu-price-${it.id}`}
                                  className="font-medium tabular-nums text-[#121614]"
                                >
                                  {money(livePrice)}
                                </span>
                              )}
                              {keys.length <= 1 && (
                                conflict?.hasAllergen ? (
                                  <BlockedAddButton conflict={conflict}
                                          testId={`menu-blocked-${it.id}`}
                                          fullSize />
                                ) : (
                                  <Button
                                    data-testid={`menu-add-${it.id}`}
                                    onClick={() => quickAdd(it, sizeKey)}
                                    size="sm"
                                    className="bg-[#121614] hover:bg-black text-white rounded-full px-4 h-10"
                                  >
                                    <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
                                    {(it.addons || []).length > 0 ? "Customise" : "Add"}
                                  </Button>
                                )
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </div>
            );
          })}
        </Accordion>
      </section>

      {addonItem && (
        <AddonDialog item={addonItem} onClose={() => setAddonItem(null)} />
      )}
    </div>
  );
}
